import { api } from '../lib/api';

export const STAGES = [
  'application',
  'screening',
  'interview',
  'offer',
  'contract',
  'onboarding',
  'training',
  'licensing',
  'live',
] as const;

export type Stage = typeof STAGES[number];

export type RecruitUpload = {
  field: 'resume' | 'license' | 'voidedCheck';
  s3Key: string;
  mimeType?: string;
  size?: number;
  uploadedAt: string;
};

export type Recruit = {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  cellPhone: string;
  state: string;
  licensedStatus: 'Licensed' | 'Non-Licensed';
  leadSource?: string;
  compPercent: number;
  salesManagerId: string;
  directUplineName: string;
  directUplineEmail: string;
  notes?: string;
  stage: Stage;
  stageEnteredAt: string;
  ownerId?: string | null;
  tcpaConsent: boolean;
  tcpaConsentAt: string;
  tcpaConsentText?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string | null;
  uploads?: RecruitUpload[];
};

export type RecruitActivity = {
  _id: string;
  recruitId: string;
  type: 'created' | 'stage_changed' | 'note_added' | 'owner_changed' | 'field_updated' | 'system';
  fromStage?: string | null;
  toStage?: string | null;
  noteBody?: string | null;
  field?: string | null;
  oldValue?: unknown;
  newValue?: unknown;
  actorId?: string | null;
  actorName?: string;
  createdAt: string;
};

export type BoardColumn = {
  stage: Stage;
  label: string;
  order: number;
  slaHours: number;
  slaBreachHours: number;
  recruits: Recruit[];
  count: number;
  slaCounts: { green: number; yellow: number; red: number };
};

export type FormMetadata = {
  states: string[];
  licensedOptions: string[];
  compTiers: number[];
  salesManagers: { id: string; label: string }[];
  consentText: string;
  maxCompPercent: number;
};

export const STAGE_COLORS: Record<Stage, string> = {
  application: 'var(--dg-stage-1)',
  screening: 'var(--dg-stage-2)',
  interview: 'var(--dg-stage-3)',
  offer: 'var(--dg-stage-4)',
  contract: 'var(--dg-stage-5)',
  onboarding: 'var(--dg-stage-6)',
  training: 'var(--dg-stage-7)',
  licensing: 'var(--dg-stage-8)',
  live: 'var(--dg-stage-9)',
};

export async function getBoard(): Promise<{ board: BoardColumn[] }> {
  const { data } = await api.get('/api/recruits/board');
  return data;
}

export async function getRecruits(params: Record<string, string> = {}): Promise<{ recruits: Recruit[] }> {
  const qs = new URLSearchParams(params).toString();
  const { data } = await api.get(`/api/recruits${qs ? `?${qs}` : ''}`);
  return data;
}

export async function getRecruit(id: string): Promise<{ recruit: Recruit; activity: RecruitActivity[] }> {
  const { data } = await api.get(`/api/recruits/${id}`);
  return data;
}

export async function moveRecruit(id: string, toStage: Stage): Promise<{ recruit: Recruit }> {
  const { data } = await api.post(`/api/recruits/${id}/move`, { toStage });
  return data;
}

export async function addNote(id: string, body: string): Promise<{ activity: RecruitActivity }> {
  const { data } = await api.post(`/api/recruits/${id}/notes`, { body });
  return data;
}

export async function getFormMetadata(): Promise<FormMetadata> {
  const { data } = await api.get('/api/form-metadata/recruit-intake');
  return data;
}

export async function createRecruit(body: Record<string, unknown>): Promise<{ recruit: Recruit }> {
  const { data } = await api.post('/api/recruits', body);
  return data;
}

export async function getUploadDownloadUrl(
  recruitId: string,
  uploadIndex: number
): Promise<{ url: string; expiresAt: string }> {
  const { data } = await api.get(`/api/recruits/${recruitId}/uploads/${uploadIndex}/download-url`);
  return data;
}

export function hoursSince(iso: string): number {
  return (Date.now() - new Date(iso).getTime()) / 3_600_000;
}

export function slaState(col: Pick<BoardColumn, 'slaHours' | 'slaBreachHours'>, enteredAt: string): 'green' | 'yellow' | 'red' {
  const h = hoursSince(enteredAt);
  if (col.slaBreachHours && h >= col.slaBreachHours) return 'red';
  if (col.slaHours && h >= col.slaHours) return 'yellow';
  return 'green';
}
